import { capturerIncident } from '../services/sentry';
import { estProduction } from './canal';

// Clé PUBLISHABLE Stripe — publique par design, au même titre que les clés RevenueCat
// (`config/purchases.ts`) : elle voyage dans le bundle et sert à identifier le compte, jamais à
// autoriser une opération. La clé SECRÈTE reste côté serveur (Edge Function coaching-payment),
// jamais ici.
//
// LA VALEUR EN DUR EST CELLE DE PRODUCTION, ET LA VARIABLE N'EST QU'UNE SURCHARGE. L'inverse — une
// variable obligatoire avec repli sur `''` — était en place et a coûté cher : `EXPO_PUBLIC_*` est
// INLINÉE AU BUILD, donc une variable absente devient `undefined` sans que rien n'échoue, ni au
// build, ni au lancement. La chaîne vide se propageait alors jusqu'à `initPaymentSheet`, et le
// paiement cassait chez l'utilisateur au moment de payer. Le défaut ne pouvait se découvrir qu'en
// production, par quelqu'un qui essayait de payer.
//
// La surcharge garde son intérêt à l'envers : poser une clé `pk_test_` dans les environnements
// `preview` et `development` pour que les builds internes n'encaissent rien de réel. Elle ne doit
// donc JAMAIS être définie dans l'environnement `production` — elle y masquerait la clé live par
// une clé de test, et rétablirait exactement le silence qu'on vient de refermer.
export const STRIPE_PUBLISHABLE_KEY =
  process.env.EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY ??
  'pk_live_51TtTmSHDsQApnw7OGTTdiwYwbzQtwXQ4Q6TzPsvdkHQ4skoNSr9eUUM3Bf4nce1Vp6Tn1RPQv5hdMdN4tATUV4rh00W1DRUwPO';

// Nom affiché dans la feuille de paiement Stripe (carte uniquement pour l'instant ;
// Apple Pay s'ajoutera ici via merchantIdentifier sans refonte).
export const STRIPE_MERCHANT_NAME = 'Horméia';

/** La clé effectivement retenue est-elle une clé de TEST ? Le préfixe suffit, Stripe le garantit. */
export function estCleDeTest(cle: string = STRIPE_PUBLISHABLE_KEY): boolean {
  return cle.startsWith('pk_test_');
}

/**
 * GARDE-FOU DE DÉMARRAGE — UNE CLÉ DE TEST DANS UN BUILD DE PRODUCTION.
 *
 * Le défaut ne se voit nulle part à l'usage : la feuille de paiement s'ouvre, la carte est
 * acceptée, l'application dit merci, et rien n'est jamais débité. Il a coûté un mois de
 * réservations de coaching, et il était visible depuis le début dans la console EAS — c'est-à-dire
 * au seul endroit où personne ne regarde tant que rien ne va mal.
 *
 * JOURNALISER, ET NON LEVER. Le choix mérite d'être argumenté, parce que l'inverse se défend :
 * encaisser dans le vide est plus grave que ne pas superviser, et on pourrait vouloir que
 * l'application refuse de démarrer.
 *
 * Elle ne le doit pas, pour la même raison qu'hier avec Sentry — brancher le LANCEMENT sur la
 * configuration d'un service, c'est se donner un second mode de panne, plus large que le premier.
 * Une clé mal réglée empêcherait alors de noter un CV, de préparer un entretien et d'ouvrir
 * l'application, pour tous, y compris ceux qui ne paieront jamais. Le rayon d'action d'un
 * garde-fou doit épouser celui de la faute : la faute est ici dans le PAIEMENT, pas dans
 * l'application.
 *
 * Et une exception ne réglerait rien de plus : elle ne serait vue que par celui qui lance le
 * build, exactement comme la console. Ce qui manquait n'était pas la sévérité, c'était la
 * DESTINATION — d'où la remontée vers Sentry, qui déclenche l'alerte au premier lancement d'un
 * build fautif, avant qu'un utilisateur ait eu le temps de croire qu'il a payé.
 *
 * Appelée explicitement au démarrage (App.tsx) plutôt qu'exécutée à l'import : Sentry doit être
 * initialisé AVANT, sans quoi la remontée serait inerte et le garde-fou muet là où il compte.
 */
export function verifierConfigurationStripe(): void {
  // LE CANAL, PAS `__DEV__` — corrigé le 20/09/2026 après confirmation sur appareil.
  //
  // `__DEV__` est faux dans TOUT build non débogué, `preview` compris. Or une `pk_test_` sur
  // `preview` n'est pas une faute : c'est l'intention même du canal, pour que les builds internes
  // n'encaissent rien de réel. Le garde-fou se déclenchait donc sur une configuration correcte —
  // faux positif observé sur le build preview 32, le seul incident de la session de test.
  //
  // Un garde-fou qui crie sur du normal ne garde rien : on apprend à l'ignorer, et il se tait le
  // jour où il a raison. Seule la PRODUCTION avec une clé de test est une faute.
  if (!estProduction() || !estCleDeTest()) return;

  console.error(
    '[stripe] CLÉ DE TEST DANS UN BUILD DE PRODUCTION : la feuille de paiement s’ouvrira et ' +
      'rien ne sera jamais débité. Supprimer EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY de ' +
      'l’environnement EAS « production » pour retomber sur la clé live.',
  );
  capturerIncident('stripe_test_key_in_production', 'config');
}
